'use client'

import { useState, useRef, useEffect, useCallback } from 'react'
import { useRouter } from '@/i18n/navigation'
import { fetchSearchResults, type SearchResult } from '@/services/search'
import { useLocale } from 'next-intl'
import { t } from '@/i18n/translations'

/* ───── Kategori Renkleri ───── */
const categoryColors: Record<string, string> = {
  'Endeks':      '#8B5CF6',
  'Türkiye':     '#E53935',
  'ABD':         '#3B82F6',
  'Avrupa':      '#A81E1E',
  'Amerikalar':  '#22C55E',
  'Asya':        '#E5A00D',
  'Ortadoğu':    '#D97706',
  'Japonya':     '#E5A00D',
  'HK/Çin':      '#E5A00D',
  'Kore':        '#E5A00D',
  'Hindistan':   '#E5A00D',
  'Döviz':       '#10B981',
  'Kripto':      '#F59E0B',
  'Emtia':       '#0EA5E9',
  'Hisse':       '#6B7280',
}

/* ───── Kategori İngilizce Karşılıkları ───── */
const categoryEnglish: Record<string, string> = {
  'Endeks':      'Index',
  'Türkiye':     'Turkey',
  'ABD':         'US',
  'Avrupa':      'Europe',
  'Amerikalar':  'Americas',
  'Asya':        'Asia',
  'Ortadoğu':    'Middle East',
  'Japonya':     'Japan',
  'HK/Çin':      'HK/China',
  'Kore':        'Korea',
  'Hindistan':   'India',
  'Döviz':       'Currency',
  'Kripto':      'Crypto',
  'Emtia':       'Commodity',
  'Hisse':       'Stock',
}

/* ───── Kategori Rozeti ───── */
function CategoryBadge({ category, lang }: { category: string; lang: string }) {
  const label = lang === 'en' ? (categoryEnglish[category] || category) : category
  return (
    <span
      className="text-[9px] font-bold px-1.5 py-0.5 rounded tracking-wide"
      style={{ backgroundColor: categoryColors[category] || 'var(--text-muted)', color: '#fff', opacity: 0.85 }}
    >
      {label}
    </span>
  )
}

/* ═══════════════════════════════════════════
   ANA BİLEŞEN
   ═══════════════════════════════════════════ */
interface SearchBarProps {
  onOpen?: () => void
  menuOpen?: boolean
  externalOpen?: boolean
  onExternalClose?: () => void
}

export default function SearchBar({ onOpen, menuOpen, externalOpen, onExternalClose }: SearchBarProps) {
  const lang = useLocale()
  const router = useRouter()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [visible, setVisible] = useState(false)
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const mobileInputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  /* ── Backend'den arama ── */
  const doSearch = useCallback((q: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      setLoading(true)
      const data = await fetchSearchResults(q)
      setResults(data)
      setLoading(false)
    }, q ? 200 : 0) // boş query → anında popüler göster
  }, [])

  useEffect(() => {
    if (open) doSearch(query)
  }, [query, open, doSearch])

  /* ── Mobile klavyeyi zorla kapat (iOS Safari + Android Chrome) ──
     iOS'ta blur() tek başına klavyeyi her zaman kapatmaz; readOnly = true
     anlık olarak input'u yazılamaz yapar ve klavye düşer. RAF sonrası geri alıyoruz. */
  const dismissKeyboard = () => {
    const active = document.activeElement
    if (!(active instanceof HTMLElement)) return
    if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) {
      const wasReadonly = active.readOnly
      active.readOnly = true
      active.blur()
      requestAnimationFrame(() => {
        active.readOnly = wasReadonly
      })
    } else {
      active.blur()
    }
  }

  const handleOpen = () => {
    onOpen?.()
    setOpen(true)
    document.body.style.overflow = window.innerWidth < 768 ? 'hidden' : ''
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setVisible(true)
        setTimeout(() => {
          if (window.innerWidth < 768) mobileInputRef.current?.focus()
          else inputRef.current?.focus()
        }, 150)
      })
    })
  }

  const handleClose = () => {
    dismissKeyboard()
    setVisible(false)
    document.body.style.overflow = ''
    setTimeout(() => {
      setOpen(false)
      setQuery('')
      setResults([])
      onExternalClose?.()
    }, 250)
  }

  useEffect(() => {
    if (externalOpen && !open) handleOpen()
    // handleOpen + open intentionally excluded — sadece externalOpen değişince trigger etmek için
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalOpen])

  const handleSelect = (item: SearchResult) => {
    dismissKeyboard()
    handleClose()
    if (item.path) {
      setTimeout(() => router.push(item.path as any), 300)
    }
  }

  useEffect(() => {
    if (menuOpen && open) handleClose()
    // handleClose + open intentionally excluded — sadece menuOpen değişimini izle
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuOpen])

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        if (open) handleClose()
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
    // handleClose her render'da yeni instance, closure içinde güncel
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) handleClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
    // Aynı: handleClose closure'da güncel
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Component unmount olursa body scroll-lock asla takılı kalmasın
  useEffect(() => {
    return () => {
      document.body.style.overflow = ''
    }
  }, [])

  const resultList = (
    <div className="overflow-y-auto flex-1">
      {!query && results.length > 0 && (
        <div className="px-4 pt-2.5 pb-1">
          <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>
            {t('search.popular', lang)}
          </span>
        </div>
      )}
      {loading ? (
        <div className="px-4 py-6 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
          {t('search.searching', lang)}
        </div>
      ) : results.length === 0 && query ? (
        <div className="px-4 py-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
          {t('search.noResults', lang)}
        </div>
      ) : (
        results.map((item) => (
          <a
            key={`${item.category}-${item.label}`}
            href="#"
            role="button"
            className="flex items-center gap-2.5 px-4 py-3 no-underline transition-colors duration-100 border-b hover:bg-[var(--bg-body)]"
            style={{ borderColor: 'var(--border-color)', WebkitTapHighlightColor: 'transparent' }}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => { e.preventDefault(); handleSelect(item) }}
          >
            <CategoryBadge category={item.category} lang={lang} />
            <span className="text-[13px] font-semibold" style={{ color: 'var(--text-main)' }}>{item.label}</span>
            <span className="text-[12px] flex-1 truncate" style={{ color: 'var(--text-muted)' }}>{item.desc}</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0" opacity="0.4">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </a>
        ))
      )}
    </div>
  )

  return (
    <div ref={containerRef} className="relative flex items-center">
      {/* Arama ikonu */}
      <button
        onClick={open ? handleClose : handleOpen}
        aria-label={t(open ? 'aria.closeSearch' : 'aria.search', lang)}
        className="flex items-center justify-center w-9 h-9 rounded-full border-none cursor-pointer transition-all duration-200 hover:opacity-60"
        style={{ background: 'transparent', color: 'var(--text-muted)' }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          {open ? (
            <>
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </>
          ) : (
            <>
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </>
          )}
        </svg>
      </button>

      {/* Desktop dropdown */}
      {open && (
        <div
          className="hidden md:block absolute top-full right-0 mt-2 w-80 rounded-xl border overflow-hidden z-50"
          style={{
            backgroundColor: 'var(--bg-card)',
            borderColor: 'var(--border-color)',
            boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0) scale(1)' : 'translateY(-8px) scale(0.98)',
            transition: 'opacity 0.25s ease, transform 0.25s ease',
          }}
        >
          <div
            className="flex items-center gap-2.5 px-3.5 h-11 border-b"
            style={{ borderColor: 'var(--border-color)' }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0" style={{ color: 'var(--text-muted)' }}>
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('search.placeholder', lang)}
              className="flex-1 bg-transparent border-none outline-none text-[13px] font-medium"
              style={{ color: 'var(--text-main)' }}
            />
            {query && (
              <button
                onClick={() => { setQuery(''); inputRef.current?.focus() }}
                aria-label={t('aria.clearSearch', lang)}
                className="shrink-0 p-1 rounded border-none cursor-pointer"
                style={{ background: 'transparent', color: 'var(--text-muted)' }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {resultList}
          </div>
        </div>
      )}

      {/* Mobil tam ekran overlay */}
      {open && (
        <div
          className="md:hidden fixed inset-0 z-[100] flex flex-col"
          style={{
            backgroundColor: 'var(--bg-card)',
            opacity: visible ? 1 : 0,
            transition: 'opacity 0.25s ease',
          }}
        >
          {/* Üst bar */}
          <div
            className="flex items-center gap-3 px-4 h-14 shrink-0 border-b"
            style={{ borderColor: 'var(--border-color)' }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0" style={{ color: 'var(--text-muted)' }}>
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              ref={mobileInputRef}
              inputMode="search"
              enterKeyHint="search"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && results.length > 0) {
                  e.preventDefault()
                  handleSelect(results[0])
                }
              }}
              placeholder={t('search.placeholder', lang)}
              className="flex-1 bg-transparent border-none outline-none text-base font-medium"
              style={{ color: 'var(--text-main)', fontSize: '16px' }}
            />
            <button
              onClick={handleClose}
              className="shrink-0 text-sm font-semibold border-none cursor-pointer px-2 py-1 rounded-lg"
              style={{ background: 'transparent', color: 'var(--accent-color)' }}
            >
              {t('search.cancel', lang)}
            </button>
          </div>

          {/* Sonuçlar */}
          {resultList}
        </div>
      )}
    </div>
  )
}
